import React, { useState, useEffect } from 'react';
import { Modal, Select, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { UserApi, Configuration } from '../../service';
import SecondaryButton from '../Button/secondaryButton';
import CustomButton from '../Button';
import styles from './ShareModal.module.scss';

interface ShareModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (shareList: string[]) => Promise<void>;
  loading: boolean;
  selectedItems?: Set<number>;
  selectedItemsDetails?: Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>;
  fileDetail?: any;
  currentEntry?: any;
}

const ShareModal: React.FC<ShareModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  loading,
  selectedItems,
  selectedItemsDetails,
  fileDetail,
  currentEntry
}) => {
  const [share, setShare] = useState<string[]>([]);
  const [shareOptions, setShareOptions] = useState<{label: string, value: string}[]>([]);
  const [shareSearchLoading, setShareSearchLoading] = useState(false);
  const userApi = new UserApi(new Configuration());

  // Initialize share list when modal opens
  useEffect(() => {
    if (visible) {
      let currentShare: string[] = [];
      
      if (selectedItems && selectedItems.size > 0) {
        // Get share list from first selected item
        const firstSelectedItem = selectedItemsDetails?.[0];
        if (firstSelectedItem?.share) {
          currentShare = firstSelectedItem.share.map((user: any) => user.email);
        }
      } else if (fileDetail) {
        // Get share list from file
        if (fileDetail.share) {
          currentShare = fileDetail.share.map((user: any) => user.email);
        }
      } else if (currentEntry) {
        // Get share list from folder/project
        if (currentEntry.share) {
          currentShare = currentEntry.share.map((user: any) => user.email);
        }
      }
      
      setShare(currentShare);
    }
  }, [visible, selectedItems, selectedItemsDetails, fileDetail, currentEntry]);

  const handleShareSearch = async (value: string) => {
    if (!value) {
      setShareOptions([]);
      return;
    }
    setShareSearchLoading(true);
    try {
      const response = await userApi.apiUserEmailsGet({ searchTerm: value }, { credentials: 'include' });
      if (response.success && response.data && response.data.emails) {
        setShareOptions(
          response.data.emails
            .filter((email: string) => email && typeof email === 'string' && email.trim())
            .map((email: string) => ({
              label: email,
              value: email
            }))
        );
      } else {
        setShareOptions([]);
      }
    } catch {
      setShareOptions([]);
    } finally {
      setShareSearchLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await onConfirm(share);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const getItemsToShare = () => {
    if (selectedItems && selectedItems.size > 0) {
      return (
        <ul className={styles.itemList}>
          {selectedItemsDetails?.map((item) => (
            <li key={item.id} className={styles.listItem}>
              <strong>{item.name}</strong> 
            </li>
          ))}
        </ul>
      );
    }

    if (fileDetail) {
      return (
        <p className={styles.singleItem}>
          <strong>{fileDetail.name}</strong> (File)
        </p>
      );
    }

    if (currentEntry) {
      return (
        <p className={styles.singleItem}>
          <strong>{currentEntry.name}</strong> ({currentEntry.entryType === 'File' ? 'File' : 'Folder'})
        </p>
      );
    }

    return <p className={styles.singleItem}>No item found</p>;
  };

  return (
    <Modal
      title={
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Share</span>
          <CloseOutlined 
            onClick={onCancel}
            className={styles.closeIcon}
          />
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      className={styles.shareModal}
      width={600}
    >
      <div className={styles.modalContent}>
        {/* Share Input */}
        <div className={styles.shareInputSection}>
          <p className={styles.sectionTitle}>
            Share With
          </p>
          <Select
            mode="multiple"
            className={styles.shareSelect}
            placeholder="Add email(s)"
            value={share}
            onChange={setShare}
            onSearch={handleShareSearch}
            options={shareOptions}
            filterOption={false}
            notFoundContent={
              <div className={styles.notFoundContent}>
                {shareSearchLoading ? "Loading..." : "No email found"}
              </div>
            }
            showSearch
            tagRender={() => <span style={{ display: 'none' }} />}
          />
          <div className={styles.emailTags}>
            {share.map(email => (
              <div
                key={email}
                className={styles.emailTag}
              >
                <span className={styles.emailAvatar}>
                  {email[0].toUpperCase()}
                </span>
                {email}
                <span
                  className={styles.removeTag}
                  onClick={() => setShare(share.filter(e => e !== email))}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Items to Share */}
        <div className={styles.itemsSection}>
          <p className={styles.sectionTitle}>
            Items to Share
          </p>
          {getItemsToShare()}
        </div>

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <SecondaryButton onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <CustomButton 
            onClick={handleConfirm}
            loading={loading}
            className={styles.shareButton}
          >
            Share
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal; 